<script lang="ts">
    import { Drawer, Button, Label, Input } from "flowbite-svelte";
    import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
    import { faTimes, faSave, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
    import { lookupDrawerStore, type LookupItem, closeLookupDrawer, saveLookup } from "../../services/stores/lookup-drawer-store.js";
    import { v4 as uuidv4 } from 'uuid';
    import {getAllLookups} from "../../services/api/data-handler.js";

    let activateClickOutside = false;
    let hidden = true;
    let items: LookupItem[] = [];
    let section: string = '';
    let sectionTitle: string = '';
    let lookupId: string = '';
    let lookupState: string = '';
    let originalItems: LookupItem[] = [];

    let transitionParams = {
        x: -320,
        duration: 200
    };

    $: {
        if ($lookupDrawerStore.open) {
            items = [...$lookupDrawerStore.items].sort((a, b) => {
                if (!a.name && !b.name) return 0;
                if (!a.name) return 1;
                if (!b.name) return -1;
                return a.name.localeCompare(b.name);
            });
            originalItems = [...$lookupDrawerStore.items]; // Store original items
            section = $lookupDrawerStore.section || '';
            lookupId = $lookupDrawerStore.lookupId || '';
            lookupState = $lookupDrawerStore.lookupState || '';
            sectionTitle = getSectionTitle(section);
            hidden = false;
        }
    }

    function getSectionTitle(text: string): string {
        return text.toLowerCase()
            .replace(/_/g, ' ') 
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    function isNewItem(item: LookupItem): boolean {
        return !originalItems.some(original => original.id === item.id);
    }

    function handleClose() {
        closeLookupDrawer();
    }

    function addNewItem() {
        if (lookupState === 'I') return;
        // Add new blank item at the end without sorting
        items = [...items, { id: uuidv4(), name: '' }];
    }

    function removeItem(index: number) {
        if (lookupState === 'I') return;
        const item = items[index];
        // Only allow deleting if it's a new item (not in originalItems)
        if (isNewItem(item)) {
            items = items.filter((_, i) => i !== index);
        }
    }

    function updateItemName(index: number, name: string) {
        if (lookupState === 'I') return;
        items = items.map((item, i) => 
            i === index ? { ...item, name } : item
        );
    }

    function handleItemBlur() {
        // Sort non-empty items alphabetically, keep empty items at the end
        items = [...items].sort((a, b) => {
            if (!a.name && !b.name) return 0;
            if (!a.name) return 1;
            if (!b.name) return -1;
            return a.name.localeCompare(b.name);
        });
    }

    async function handleSave() {
        if (lookupState === 'I') return;
        const validItems = items.filter(item => item.name.trim() !== '');
        await saveLookup(section, validItems, lookupId);
        handleClose();
        await getAllLookups();
    }

    $: if ($lookupDrawerStore.open !== !hidden) {
        hidden = !$lookupDrawerStore.open;
    }
</script>

<div class="drawer-container">
    <Drawer
        id="lookup-drawer"
        class="object-drawer"
        {activateClickOutside}
        transitionType="fly"
        {transitionParams}
        placement="left"
        backdrop={true}
        bind:hidden
    >
        <div class="drawer-header">
            <div class="drawer-title-container">
                <h2>{sectionTitle}</h2>
            </div>
            <Button class="drawer-header-button" on:click={handleClose}>
                <FontAwesomeIcon icon={faTimes} />
            </Button>
        </div>

        <div class="p-4 flex flex-col" style="height:calc(100vh - 14rem);">
            {#if lookupState === 'D' || lookupState === 'A'}
                <div class="mb-2">
                    <Button class="primary-button" size="xs" color="primary" on:click={addNewItem}>
                        <FontAwesomeIcon icon={faPlus} class="mr-2" />Add Item
                    </Button>
                </div>
            {/if}

            <div class="overflow-y-auto">
                {#each items as item, index}
                    <div class="flex items-center">
                        <div>
                            <Input
                                id="item-{index}"
                                class="lookup-input"
                                placeholder="Item name"
                                value={item.name}
                                on:input={(e) => updateItemName(index, e?.target?.value)}
                                on:blur={handleItemBlur}
                                disabled={lookupState === 'I'}
                            />
                        </div>
                        {#if (lookupState === 'D' || (lookupState === 'A' && isNewItem(item)))}
                            <Button
                                class="lookup-button icon-only"
                                on:click={() => removeItem(index)}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        {/if}
                    </div>
                {/each}
            </div>
        </div>

        <div class="p-4 flex justify-end gap-2 drawer-footer">
            {#if lookupState === 'D' || lookupState === 'A'}
                <Button class="save-button"size="xs" color="primary" on:click={handleSave}>
                    <FontAwesomeIcon icon={faSave} class="mr-2" />Save
                </Button>
            {/if}
            <Button class="cancel-button" size="xs" color="light" on:click={handleClose}>
                <FontAwesomeIcon icon={faTimes} class="mr-2" />Cancel
            </Button>
        </div>
    </Drawer>
</div>